'use strict';
import Search from '@components/Common/Search.js';
import Loader from '@components/Loading/Loader.js';
import UIIcon from '@icons/UIIcon.js';
import {
  isVlmImageOversized,
  oversizedVlmImageNotice,
} from '@pages/SpatialVision/utils/imageUploadLimits.js';
import ProjectsState, { LoadedFolderIds } from '@states/Projects.js';
import { SpatialVisionState } from '@states/SpatialVision.js';
import { AvailableModelState } from '@states/Viewer.js';
import { UIButton, UIDiv, UIImage, UIText } from '@ui';
import { getCadFileModelId, getReconstructionId } from '@utils/cadFileModel.js';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'];

const getExtension = value =>
  String(value || '')
    .split('?')[0]
    .split('#')[0]
    .split('.')
    .pop()
    .toLowerCase();

const isImageReconstruction = reconstruction =>
  IMAGE_EXTENSIONS.includes(
    getExtension(
      reconstruction.filePath ||
        reconstruction.fileName ||
        reconstruction.fileOriginalName,
    ),
  );

class AiAnalyzerDetectPopover extends UIDiv {
  constructor({ getSelection, onChange }) {
    super();
    this.addClass('w-96 h-[520px] rounded-lg overflow-hidden flex flex-col');
    this.getSelection = getSelection;
    this.onChange = onChange;
    this.projects = ProjectsState.projects;
    this.currentProject = null;
    this.searchTerm = '';
    this.isBuilding = false;
    this.validationNotice = null;

    ProjectsState.subscribe(this.#buildUI.bind(this));
    this.#buildUI();
  }

  #selection() {
    return this.getSelection?.() || {};
  }

  refreshSelection() {
    this.#buildUI();
  }

  #isMultiSelectMode() {
    return (
      this.#selection().runMode === 'sequence' ||
      SpatialVisionState.spatialVision.selectedServiceTier === 'cloud'
    );
  }

  #selectedImages() {
    const selection = this.#selection();
    if (selection.images?.length > 0) return selection.images;
    return selection.image ? [selection.image] : [];
  }

  #sameImage(left, right) {
    const leftModelId = getCadFileModelId(left);
    const rightModelId = getCadFileModelId(right);
    if (leftModelId || rightModelId) {
      return String(leftModelId) === String(rightModelId);
    }
    return (
      left?.path === right?.path &&
      String(left?.taskId) === String(right?.taskId)
    );
  }

  #isSelected(file) {
    return this.#selectedImages().some(image => this.#sameImage(image, file));
  }

  #selectImage(file) {
    const image = { ...file, source: 'project' };
    const selectedImages = this.#selectedImages();
    const exists = selectedImages.some(selected =>
      this.#sameImage(selected, image),
    );
    if (!exists && isVlmImageOversized(image)) {
      this.validationNotice = oversizedVlmImageNotice(
        [image],
        selectedImages.length,
      );
      this.#buildUI();
      return;
    }
    this.validationNotice = null;
    if (!this.#isMultiSelectMode()) {
      this.#updateSelection({ image, images: [image] });
      return;
    }

    const images = exists
      ? selectedImages.filter(selected => !this.#sameImage(selected, image))
      : [...selectedImages, image].slice(0, 10);

    this.#updateSelection({
      image: images[0] || null,
      images,
    });
  }

  #updateSelection(update) {
    this.onChange?.({
      ...this.#selection(),
      ...update,
    });
    this.#buildUI();
  }

  #buildUI() {
    if (this.isBuilding) return;
    this.isBuilding = true;
    this.projects = ProjectsState.projects;
    this.clear();
    this.add(this.#buildHeader());
    if (this.validationNotice) this.add(this.#buildValidationNotice());
    this.listContainer = new UIDiv();
    this.listContainer.addClass('p-2 space-y-2 overflow-y-auto flex-1');
    this.add(this.listContainer);

    if (this.currentProject) this.#buildImagesList();
    else this.#buildProjectsList();

    this.isBuilding = false;
  }

  #buildValidationNotice() {
    const notice = new UIDiv().setClass(
      'mx-3 mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-900',
    );
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    const copy = new UIDiv()
      .setClass('min-w-0 flex-1')
      .add(
        new UIText(this.validationNotice.title).setClass(
          'block text-xs font-semibold leading-snug',
        ),
        new UIText(this.validationNotice.description).setClass(
          'mt-0.5 block text-[11px] leading-snug text-amber-800',
        ),
      );
    notice.add(
      new UIText('!').setClass(
        'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white',
      ),
      copy,
      new UIButton()
        .setClass(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded text-[16px] leading-none text-amber-700 hover:bg-amber-100 hover:text-amber-900',
        )
        .setTitle('Dismiss image size message')
        .setAttribute('aria-label', 'Dismiss image size message')
        .add(new UIText('×'))
        .onClick(event => {
          event.stopPropagation();
          this.validationNotice = null;
          this.#buildUI();
        }),
    );
    return notice;
  }

  #buildHeader() {
    const header = new UIDiv();
    header.addClass(
      'sticky top-0 bg-white border-b border-gray-200 p-3 flex-shrink-0 space-y-3',
    );

    const search = new Search({
      handleSearch: searchValue => {
        this.searchTerm = searchValue.toLowerCase();
        this.#buildUI();
      },
      placeholder: `${i18next.t('common.search')}...`,
    });

    header.add(
      new UIDiv()
        .setClass('flex items-center gap-2')
        .add(
          new UIDiv()
            .setClass(
              'flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700',
            )
            .add(this.#icon('image')),
          new UIDiv()
            .setClass('min-w-0')
            .add(
              new UIText('Select an image').setClass(
                'text-sm font-semibold text-slate-900 block',
              ),
              new UIText('Choose a source folder to run on').setClass(
                'text-xs text-slate-500 block',
              ),
            ),
        ),
      search,
    );

    if (this.currentProject) {
      header.add(
        new UIDiv()
          .setClass('flex items-center gap-2 border-t border-gray-100 pt-2')
          .add(
            new UIButton()
              .setClass('p-1.5 rounded hover:bg-slate-100 text-slate-600')
              .setTitle('Back to projects')
              .add(this.#icon('arrow-left'))
              .onClick(e => {
                e.stopPropagation();
                this.currentProject = null;
                this.searchTerm = '';
                this.#buildUI();
              }),
            new UIText(this.currentProject.name).setClass(
              'text-sm font-medium text-slate-700 truncate',
            ),
          ),
      );
    }

    return header;
  }

  #buildProjectsList() {
    this.listContainer.clear();

    if (!this.projects || this.projects.isLoading) {
      this.listContainer.add(this.#loadingBlock());
      return;
    }

    const folders = this.projects.folders || [];
    const filteredFolders = folders.filter(folder =>
      folder.name?.toLowerCase().includes(this.searchTerm),
    );

    if (!filteredFolders.length) {
      this.listContainer.add(
        this.#emptyBlock(
          folders.length ? 'No projects found' : 'No projects available',
        ),
      );
      return;
    }

    const section = new UIDiv().addClass('space-y-1 mb-4');
    filteredFolders.forEach(folder => {
      const isLoaded = LoadedFolderIds.loadedFolderIds?.includes(folder.id);
      const imageCount = isLoaded
        ? this.#getImageFilesForTasks(
            (this.projects.tasks || []).filter(
              task => task.parentId === folder.id,
            ),
          ).length
        : null;

      const item = new UIDiv()
        .setClass(
          'flex items-center gap-3 p-3 py-2.5 rounded cursor-pointer hover:bg-slate-50 transition-colors',
        )
        .add(
          this.#icon('folder'),
          new UIDiv()
            .setClass('flex-1 min-w-0')
            .add(
              new UIText(folder.name).setClass(
                'text-sm font-medium text-slate-700 truncate block',
              ),
              new UIText(
                isLoaded
                  ? `${imageCount} image file${imageCount === 1 ? '' : 's'}`
                  : 'Open to load image files',
              ).setClass('text-xs text-slate-500 truncate block'),
            ),
          this.#icon('arrow-left', { className: 'rotate-180' }),
        )
        .onClick(async e => {
          e.stopPropagation();
          this.currentProject = folder;
          this.#buildUI();
          if (!LoadedFolderIds.loadedFolderIds?.includes(folder.id)) {
            await ProjectsState.getProjectById(folder.id);
            this.#buildUI();
          }
        });
      section.add(item);
    });
    this.listContainer.add(section);
  }

  #buildImagesList() {
    this.listContainer.clear();

    const isLoaded = LoadedFolderIds.loadedFolderIds?.includes(
      this.currentProject.id,
    );
    if (!isLoaded) {
      this.listContainer.add(this.#loadingBlock());
      return;
    }

    const tasks = (this.projects.tasks || []).filter(
      task => task.parentId === this.currentProject.id,
    );
    const files = this.#getImageFilesForTasks(tasks).filter(
      file =>
        file.originalName.toLowerCase().includes(this.searchTerm) ||
        file.taskName.toLowerCase().includes(this.searchTerm),
    );

    if (!files.length) {
      this.listContainer.add(
        this.#emptyBlock(
          tasks.length
            ? 'No image reconstruction files found'
            : 'No tasks found in this project',
        ),
      );
      return;
    }

    const selectedCount = this.#selectedImages().length;
    if (this.#isMultiSelectMode()) {
      this.listContainer.add(
        new UIText(
          `${selectedCount}/10 selected. Choose more images or click selected images to remove them.`,
        ).setClass('block px-2 pb-2 text-xs text-slate-500'),
      );
    }
    const section = new UIDiv().addClass('space-y-1 mb-4');
    files.forEach(file => {
      const isSelected = this.#isSelected(file);
      const item = new UIDiv()
        .setClass(
          [
            'flex items-center gap-3 rounded border p-2 cursor-pointer transition-colors',
            isSelected
              ? 'bg-[var(--o-hive-primary-20)] border-[var(--o-hive-primary-80)]'
              : 'border-transparent hover:bg-slate-50 hover:border-slate-300',
          ].join(' '),
        )
        .add(
          this.#imagePreview(file, isSelected),
          new UIDiv()
            .setClass('flex-1 min-w-0')
            .add(
              new UIText(file.originalName).setClass(
                'text-sm font-medium text-slate-700 truncate block',
              ),
              new UIText(`from: ${file.taskName}`).setClass(
                'text-xs text-slate-500 truncate block',
              ),
            ),
        )
        .onClick(e => {
          e.stopPropagation();
          this.#selectImage(file);
        });
      section.add(item);
    });
    this.listContainer.add(section);
  }

  #getImageFilesForTasks(tasks) {
    const files = [];
    tasks.forEach(task => {
      (task.reconstructions || [])
        .filter(isImageReconstruction)
        .forEach(reconstruction => {
          const viewerModel =
            this.#viewerModelForReconstruction(reconstruction);
          files.push({
            taskId: task.id,
            cadFileModelId:
              getCadFileModelId(reconstruction) ||
              getCadFileModelId(viewerModel),
            reconstructionId:
              getReconstructionId(reconstruction) ||
              getReconstructionId(viewerModel),
            taskName: task.name || 'Untitled task',
            path: reconstruction.filePath,
            thumbnail: reconstruction.thumbnail || reconstruction.filePath,
            originalName:
              reconstruction.fileOriginalName ||
              reconstruction.fileName ||
              reconstruction.filePath?.split('/').pop() ||
              'image',
            fileSize: reconstruction.fileSize,
          });
        });
    });
    return files;
  }

  #viewerModelForReconstruction(reconstruction) {
    const reconstructionId = getReconstructionId(reconstruction);
    const path = reconstruction?.filePath;
    return (
      (AvailableModelState.availableModel || []).find(model => {
        const sameReconstruction =
          reconstructionId &&
          String(getReconstructionId(model)) === String(reconstructionId);
        const samePath = path && model?.filePath === path;
        return sameReconstruction || samePath;
      }) || null
    );
  }

  #imagePreview(file, isSelected) {
    const wrapper = new UIDiv().setClass(
      'relative h-14 w-16 flex-shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100',
    );
    const thumbnail = new UIImage(file.thumbnail || file.path);
    thumbnail.setAlt(file.originalName);
    thumbnail.setClass('h-full w-full object-cover');
    thumbnail.dom.onerror = () => {
      thumbnail.setSrc('/static/images/icons/image.svg');
      thumbnail.setClass('h-full w-full object-contain p-3');
    };
    wrapper.add(thumbnail);

    if (isSelected) {
      wrapper.add(
        new UIDiv()
          .setClass(
            'absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.22)] text-white',
          )
          .add(
            new UIDiv()
              .setClass(
                'flex h-6 w-6 items-center justify-center rounded-full bg-[var(--o-hive-primary-80)] shadow-sm',
              )
              .add(this.#icon('check', { size: 14 })),
          ),
      );
    }

    return wrapper;
  }

  #loadingBlock() {
    return new UIDiv()
      .setClass('flex items-center justify-center py-6')
      .add(new Loader());
  }

  #emptyBlock(message) {
    return new UIDiv()
      .setClass('text-sm text-slate-500 px-2 py-3')
      .add(new UIText(message));
  }

  #icon(name, options = {}) {
    return new UIIcon(name, {
      color: 'none',
      strokeColor: 'currentColor',
      size: 16,
      ...options,
    });
  }
}

export { AiAnalyzerDetectPopover };
